/**
 * Canicule : scroll reveal, stagger, editorial carousel, micro-motion
 * Respects prefers-reduced-motion
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STAGGER_MS = 80;

  /* ── Scroll reveal (Intersection Observer) ── */
  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-canicule-reveal]');
    if (!elements.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('canicule-reveal--visible');
        revealStaggerChildren(el);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var delay = entry.target.getAttribute('data-reveal-delay') || 0;
            setTimeout(function () {
              entry.target.classList.add('canicule-reveal--visible');
              revealStaggerChildren(entry.target);
            }, Number(delay));
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  function revealStaggerChildren(root) {
    var staggers = root.querySelectorAll('[data-canicule-reveal-stagger]');
    staggers.forEach(function (container) {
      container.classList.add('canicule-reveal-stagger--visible');
      var children = container.children;
      for (var i = 0; i < children.length; i++) {
        children[i].style.transitionDelay = i * STAGGER_MS + 'ms';
      }
    });
  }

  /* ── Stagger reveal for standalone containers ── */
  function initStaggerReveal() {
    var containers = document.querySelectorAll('[data-canicule-reveal-stagger]');
    if (!containers.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      containers.forEach(function (container) {
        container.classList.add('canicule-reveal-stagger--visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('canicule-reveal-stagger--visible');
            var children = entry.target.children;
            for (var i = 0; i < children.length; i++) {
              children[i].style.transitionDelay = i * STAGGER_MS + 'ms';
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    containers.forEach(function (el) {
      if (!el.closest('[data-canicule-reveal]')) {
        observer.observe(el);
      }
    });
  }

  /* ── Heading word fade-in ── */
  function initHeadingFade() {
    var headings = document.querySelectorAll('[data-heading-fade]');
    headings.forEach(function (heading) {
      if (heading.querySelector('.canicule-heading-fade__word')) return;
      var text = heading.textContent.trim();
      if (!text) return;
      var words = text.split(/\s+/);
      heading.textContent = '';
      heading.classList.add('canicule-heading-fade');
      words.forEach(function (word, i) {
        var span = document.createElement('span');
        span.className = 'canicule-heading-fade__word';
        span.textContent = word;
        span.style.transitionDelay = i * 60 + 'ms';
        heading.appendChild(span);
        if (i < words.length - 1) {
          heading.appendChild(document.createTextNode(' '));
        }
      });
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      headings.forEach(function (h) {
        h.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    headings.forEach(function (h) {
      observer.observe(h);
    });
  }

  /* ── Editorial parallax (CSS transform, subtle) ── */
  function initEditorialParallax() {
    if (prefersReducedMotion) return;

    var panels = document.querySelectorAll('[data-editorial-parallax]');
    if (!panels.length) return;

    var mqDesktop = window.matchMedia('(min-width: 750px)');

    panels.forEach(function (panel) {
      function onScroll() {
        if (!mqDesktop.matches) {
          panel.style.transform = '';
          return;
        }
        var rect = panel.getBoundingClientRect();
        var viewH = window.innerHeight || 1;
        if (rect.bottom < 0 || rect.top > viewH) return;
        var progress = (viewH - rect.top) / (viewH + rect.height);
        var offset = (progress - 0.5) * 16;
        panel.style.transform = 'translate3d(0, ' + offset.toFixed(1) + 'px, 0)';
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });
  }

  /* ── Editorial carousel (3s auto, pause on hover) ── */
  function initEditorialCarousel() {
    var carousels = document.querySelectorAll('[data-editorial-carousel]');
    carousels.forEach(function (carousel) {
      var slides = carousel.querySelectorAll('.canicule-editorial-book__carousel-slide');
      if (slides.length < 2) return;

      if (prefersReducedMotion) {
        slides[0].classList.add('is-active');
        return;
      }

      var index = 0;
      var paused = false;
      var interval;

      function showSlide(i) {
        slides.forEach(function (s) {
          s.classList.remove('is-active');
        });
        slides[i].classList.add('is-active');
      }

      function tick() {
        if (paused) return;
        index = (index + 1) % slides.length;
        showSlide(index);
      }

      carousel.addEventListener('mouseenter', function () {
        paused = true;
      });
      carousel.addEventListener('mouseleave', function () {
        paused = false;
      });

      interval = setInterval(tick, 3000);
      carousel._carouselInterval = interval;
    });
  }

  /* ── Sticky header scroll state ── */
  function initStickyHeader() {
    var headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;

    var scrolled = false;
    function onScroll() {
      var shouldScroll = window.scrollY > 24;
      if (shouldScroll !== scrolled) {
        scrolled = shouldScroll;
        headerGroup.classList.toggle('canicule-header--scrolled', scrolled);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mega menu interactions ── */
  function initMegaMenu() {
    var CLOSE_DELAY_MS = 250;
    var desktopMq = window.matchMedia('(min-width: 990px)');

    function isDesktop() {
      return desktopMq.matches;
    }

    function closeDropdown(wrapper) {
      var trigger = wrapper.querySelector('[data-mega-trigger]');
      var panelId = trigger && trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      var item = wrapper.closest('.canicule-mega-menu__item');
      if (!trigger || !panel) return;

      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      if (item) item.classList.remove('is-open');
      wrapper._megaOpen = false;
    }

    function openDropdown(wrapper) {
      var trigger = wrapper.querySelector('[data-mega-trigger]');
      var panelId = trigger && trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      var item = wrapper.closest('.canicule-mega-menu__item');
      if (!trigger || !panel) return;

      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (item) item.classList.add('is-open');
      wrapper._megaOpen = true;
    }

    function clearCloseTimer(wrapper) {
      if (wrapper._megaCloseTimer) {
        clearTimeout(wrapper._megaCloseTimer);
        wrapper._megaCloseTimer = null;
      }
    }

    function scheduleClose(wrapper) {
      clearCloseTimer(wrapper);
      wrapper._megaCloseTimer = setTimeout(function () {
        closeDropdown(wrapper);
      }, CLOSE_DELAY_MS);
    }

    var menus = document.querySelectorAll('.canicule-mega-menu');
    menus.forEach(function (menu) {
      var toggle = menu.querySelector('[data-mega-toggle]');
      var mobilePanel = menu.querySelector('[data-mega-mobile-panel]');
      var dropdowns = menu.querySelectorAll('[data-mega-menu]');

      if (toggle && mobilePanel) {
        toggle.addEventListener('click', function () {
          var expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          mobilePanel.hidden = expanded;
          document.documentElement.classList.toggle('canicule-nav-drawer-open', !expanded);
        });

        var drawerClose = menu.querySelector('[data-mega-drawer-close]');
        if (drawerClose) {
          drawerClose.addEventListener('click', function () {
            toggle.setAttribute('aria-expanded', 'false');
            mobilePanel.hidden = true;
            document.documentElement.classList.remove('canicule-nav-drawer-open');
          });
        }
      }

      dropdowns.forEach(function (wrapper) {
        var trigger = wrapper.querySelector('[data-mega-trigger]');
        var panelId = trigger && trigger.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (!trigger || !panel) return;

        wrapper._megaOpen = false;

        wrapper.addEventListener('mouseenter', function () {
          if (!isDesktop()) return;
          clearCloseTimer(wrapper);
          openDropdown(wrapper);
        });

        wrapper.addEventListener('mouseleave', function () {
          if (!isDesktop()) return;
          scheduleClose(wrapper);
        });

        trigger.addEventListener('click', function (e) {
          if (isDesktop()) {
            e.preventDefault();
            clearCloseTimer(wrapper);
            if (!wrapper._megaOpen) openDropdown(wrapper);
            return;
          }
          if (wrapper._megaOpen) closeDropdown(wrapper);
          else openDropdown(wrapper);
        });

        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            clearCloseTimer(wrapper);
            openDropdown(wrapper);
            var firstLink = panel.querySelector('a, button');
            if (firstLink) firstLink.focus();
          } else if (e.key === 'Escape' && wrapper._megaOpen) {
            e.preventDefault();
            closeDropdown(wrapper);
            trigger.focus();
          }
        });

        panel.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && wrapper._megaOpen) {
            e.preventDefault();
            closeDropdown(wrapper);
            trigger.focus();
          }
        });
      });

      document.addEventListener('click', function (e) {
        if (menu.contains(e.target)) return;
        dropdowns.forEach(function (wrapper) {
          clearCloseTimer(wrapper);
          closeDropdown(wrapper);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        dropdowns.forEach(function (wrapper) {
          if (!wrapper._megaOpen) return;
          clearCloseTimer(wrapper);
          closeDropdown(wrapper);
          var trigger = wrapper.querySelector('[data-mega-trigger]');
          if (trigger) trigger.focus();
        });
      });
    });
  }

  /* ── Announcement bar rotation ── */
  function initAnnouncementBar() {
    var tracks = document.querySelectorAll('[data-announcement-track]');
    if (!tracks.length || prefersReducedMotion) return;

    tracks.forEach(function (track) {
      var slides = track.querySelectorAll('[data-announcement-slide]');
      if (slides.length < 2) return;

      var speed = parseInt(track.getAttribute('data-rotation-speed'), 10);
      if (isNaN(speed) || speed < 3) speed = 5;
      var interval = speed * 1000;

      var index = 0;
      var paused = false;
      track.addEventListener('mouseenter', function () { paused = true; });
      track.addEventListener('mouseleave', function () { paused = false; });

      setInterval(function () {
        if (paused || document.hidden) return;
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, interval);
    });
  }

  /* ── Product anatomy hotspots + scroll pulse ── */
  function initAnatomyHotspots() {
    var hotspots = document.querySelectorAll('[data-anatomy-hotspot]');
    hotspots.forEach(function (hs) {
      hs.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasActive = hs.classList.contains('is-active');
        hotspots.forEach(function (h) {
          h.classList.remove('is-active');
        });
        if (!wasActive) hs.classList.add('is-active');
      });
    });
    document.addEventListener('click', function () {
      hotspots.forEach(function (h) {
        h.classList.remove('is-active');
      });
    });

    if (prefersReducedMotion || !hotspots.length || !('IntersectionObserver' in window)) return;

    var diagram = document.querySelector('[data-anatomy-diagram]');
    if (!diagram) return;

    var pulsed = false;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !pulsed) {
            pulsed = true;
            hotspots.forEach(function (hs, i) {
              setTimeout(function () {
                hs.classList.add('is-scroll-pulse');
                setTimeout(function () {
                  hs.classList.remove('is-scroll-pulse');
                }, 1200);
              }, i * 200);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(diagram);
  }

  /* ── PDP gallery thumbnail crossfade polish ── */
  function initGalleryCrossfade() {
    if (prefersReducedMotion) return;

    var galleries = document.querySelectorAll('.product-information media-gallery, .product-information .media-gallery');
    galleries.forEach(function (gallery) {
      var mainImages = gallery.querySelectorAll('.product-media-container img, [data-main-media] img');
      mainImages.forEach(function (img) {
        img.style.transition = 'opacity 0.35s ease';
      });
    });
  }

  /* ── Hero parallax (subtle, desktop only) ── */
  function initHeroParallax() {
    if (prefersReducedMotion) return;

    var heroes = document.querySelectorAll('[data-hero-parallax]');
    if (!heroes.length) return;

    var mqDesktop = window.matchMedia('(min-width: 750px)');

    function bindHero(hero) {
      var layer = hero.querySelector('[data-hero-parallax-layer]');
      if (!layer) return;

      function onScroll() {
        if (!mqDesktop.matches) {
          layer.style.transform = '';
          return;
        }
        var rect = hero.getBoundingClientRect();
        var viewH = window.innerHeight || 1;
        if (rect.bottom < 0 || rect.top > viewH) return;
        var progress = (viewH - rect.top) / (viewH + rect.height);
        var offset = (progress - 0.5) * 24;
        layer.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    heroes.forEach(bindHero);
  }

  /* ── Product carousel slow auto-scroll ── */
  function initProductCarouselAutoScroll() {
    if (prefersReducedMotion) return;

    var carousels = document.querySelectorAll('.product-list-section .resource-list--carousel');
    carousels.forEach(function (list) {
      var paused = false;
      list.addEventListener('mouseenter', function () {
        paused = true;
      });
      list.addEventListener('mouseleave', function () {
        paused = false;
      });
      list.addEventListener('touchstart', function () {
        paused = true;
      }, { passive: true });

      setInterval(function () {
        if (paused || list.scrollWidth <= list.clientWidth) return;
        var item = list.querySelector('.resource-list__item');
        var step = item ? item.offsetWidth + 8 : 280;
        if (list.scrollLeft + list.clientWidth >= list.scrollWidth - 4) {
          list.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          list.scrollBy({ left: step, behavior: 'smooth' });
        }
      }, 5000);
    });
  }

  function init() {
    if (window._caniculeAnimationsInit) return;
    window._caniculeAnimationsInit = true;

    initScrollReveal();
    initStaggerReveal();
    initHeadingFade();
    initEditorialParallax();
    initEditorialCarousel();
    initStickyHeader();
    initMegaMenu();
    initAnnouncementBar();
    initAnatomyHotspots();
    initGalleryCrossfade();
    initHeroParallax();
    initProductCarouselAutoScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
